import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ttLogo from "@src/assets/svg/tt-logo.svg";
import linkIcon from "@src/assets/svg/link.svg";
import styles from "./machine-overview-widget.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import {
  DURATION_GAUGE_WIDGET_MS,
  isoToDurationMs,
  resolvePollingMs,
} from "../constants";
import {
  MACHINE_OVERVIEW_FIXED_BINDING_KEYS,
  MachineOverviewDisplayMode,
  REQUIRED_METRIC_SLOTS,
  computeMetric,
  getStatusBadgeStyle,
  isMachineOverviewSemantics,
  matchStatusEntry,
  metricSlotField,
  resolveRowLabel as resolveRowLabelRaw,
  sortMetricsForDisplay,
} from "./machine-overview-widget-config";
import type {
  MachineOverviewMetric,
  MachineOverviewMetricSlot,
  MachineOverviewRow,
  QueryBinding,
  QuerySpec,
} from "@src/types/dashboards";
import { getWidgetDataSource } from "../widget-query";
import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import { useRefetchWhenVisible } from "@src/hooks/use-check-widget-in-viewport";
import { useI18N } from "@src/language/language-context";
import Spinner from "@src/components/atoms/spinner/spinner";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { getDashboardPath } from "@src/routes/dashboard-paths";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitLabelOf } from "@src/cloud-connect/units/utils/unit-label-of";
import { applyFieldTransforms } from "../utils/value-scaling";

// --- Polling clock + per-tick fetchers ---------------------------------
//
// One widget-level clock drives every fetch in this widget (every fixed
// row + every dynamic row + every MetricRow). Children re-fetch when
// the clock ticks, so all slots that share a tickId are guaranteed to
// have been triggered by the same polling timeout. Per-fetch promises
// are NOT cancelled when the next tick fires — we want each tick's
// result stamped with that tickId so the metric value can be gated on
// "all required slots returned for the same tick" (mixed-tick values
// would be wrong). `setValuesByTick` keeps at most the last 8 ticks so
// memory can't grow unbounded.
/** `value` is a string for `data_type === "string"` nodes (status
 *  nodes, plain text rows). Numeric consumers narrow it themselves via
 *  `typeof x === "number"`. */
type QueryValue = { time: string; value: number | string };

type TickFetcherResult = {
  value: number | string | null;
  lastTimestamp: string | null;
  hasNoData: boolean;
  /** True while a tick has fired and we're still waiting on its fetch
   *  for THIS slot. False while tickId is 0 (idle) or the result for
   *  the current tick has arrived. */
  isLoading: boolean;
  error?: string;
  tickId: number;
};

/** Widget-level polling clock. Owns ONE `setInterval` for the whole
 *  machine-overview; children re-fetch each tick. tickId starts at 0
 *  (idle — no fetch attempts) and increments on mount, then every
 *  pollingMs, while the widget is in viewport. Off-screen widgets drop
 *  the interval but the inViewport check inside each fetcher makes them
 *  no-op, so re-entry doesn't accidentally double-fire. */
const useWidgetPollingClock = (
  pollingMs: number | null,
  inViewport: boolean,
): number => {
  const [tickId, setTickId] = useState(0);
  useEffect(() => {
    if (pollingMs == null || !inViewport) return;
    // Initial tick fires on mount (first paint, pre-interval).
    setTickId((t) => t + 1);
    const id = setInterval(() => setTickId((t) => t + 1), pollingMs);
    return () => clearInterval(id);
  }, [pollingMs, inViewport]);
  return tickId;
};

/** Single-slot tick fetcher. Returns the latest value + lastTimestamp
 *  stamped with the tickId it was fetched for. Same shape as the old
 *  `useLastWidgetValue` (value / lastTimestamp / hasNoData / error) so
 *  callers can read it identically; only the polling cadence changes
 *  (now widget-level, not per-hook). */
const useTickFetchValue = ({
  tickId,
  query,
  durationMs,
  inViewport,
  preview,
}: {
  tickId: number;
  query: QuerySpec | null | undefined;
  durationMs: number;
  inViewport: boolean;
  preview: boolean;
}): TickFetcherResult => {
  const [state, setState] = useState<TickFetcherResult>({
    value: null,
    lastTimestamp: null,
    hasNoData: false,
    isLoading: false,
    error: undefined,
    tickId: 0,
  });
  // Visibility bridge — off-screen fetches pause at the hook level.
  const latestLoadRef = React.useRef<(() => void) | null>(null);
  const inViewportRef = useRefetchWhenVisible(inViewport, latestLoadRef);

  // First-ever-load flag. Mirrors the original `useLastWidgetValue`
  // behavior: spinner shows ONLY on the very first fetch — subsequent
  // polling ticks replace the value in place (silent refresh, no
  // flicker). The ref flips once and never resets for the hook's
  // lifetime.
  const hasShownInitialLoaderRef = React.useRef(false);
  // Epoch counter bumped on every effect run so a slow in-flight
  // response from an older run can't overwrite a fresher fetch's
  // write (same-tickId race when the polling clock bumps twice in
  // close succession).
  const fetchEpochRef = React.useRef(0);

  // `query`/`durationMs`/`preview` are captured via refs because the
  // fetch should re-fire ONLY on tickId, not on every parent rerender
  // (sliceQueryForField returns a fresh object identity each call,
  // which would otherwise ping-pong the effect). Caller-provided
  // objects stay current without driving a fetching storm.
  const queryRef = React.useRef(query);
  queryRef.current = query;
  const durationMsRef = React.useRef(durationMs);
  durationMsRef.current = durationMs;
  const previewRef = React.useRef(preview);
  previewRef.current = preview;
  // Data-source key extracted from sliced binding fields. Stable across
  // re-renders when the bound node hasn't changed — so a save-cycle
  // re-point of the bound node bumps this key (and the dep below) and
  // re-fires the fetch, exactly like a polling tick would.
  const queryKey =
    query == null
      ? ""
      : `${query.source ?? ""}|${query.bindings?.[0]?.source ?? ""}|${
          query.bindings?.[0]?.address ?? ""
        }`;

  useEffect(() => {
    const epoch = ++fetchEpochRef.current;
    const currentQuery = queryRef.current;
    const currentDurationMs = durationMsRef.current;
    const currentPreview = previewRef.current;
    if (tickId === 0) return;
    if (currentPreview || !currentQuery) {
      setState((s) => ({
        ...s,
        tickId,
        value: null,
        lastTimestamp: null,
        hasNoData: false,
        isLoading: false,
        error: undefined,
      }));
      return;
    }
    const ds = getWidgetDataSource(currentQuery);
    if (!ds) {
      setState((s) => ({
        ...s,
        tickId,
        value: null,
        lastTimestamp: null,
        hasNoData: false,
        isLoading: false,
      }));
      return;
    }
    // Mark the slot as in-flight ONLY on the very first tick. After
    // that, polling ticks update the value in place — the spinner
    // flashes once on initial mount and stays gone, matching the
    // old `useLastWidgetValue` behavior so users don't see a flicker
    // every `pollingMs`.
    if (!hasShownInitialLoaderRef.current) {
      hasShownInitialLoaderRef.current = true;
      setState((s) => ({ ...s, tickId, isLoading: true }));
    } else {
      // Subsequent ticks: just stamp the new tickId so out-of-order
      // writes can be detected by callers' guard.
      setState((s) => ({ ...s, tickId }));
    }
    // Drop late responses from fetches that have already been superseded.
    // Every tick and every save-cycle rebind bumps the epoch before
    // kicking off load(), so without this bail the older fetch's setState
    // could clobber the newer one's data — including the same-tickId case
    // where save rebinds land inside one polling window.
    const guard = (
      stamp: number,
      next: Omit<TickFetcherResult, "tickId">,
    ): void => {
      if (fetchEpochRef.current !== epoch) return;
      setState((s) => (s.tickId > stamp ? s : { ...next, tickId: stamp }));
    };
    const load = async () => {
      if (!inViewportRef.current || document.hidden) return;
      try {
        const to = new Date().toISOString();
        const from = new Date(Date.now() - currentDurationMs).toISOString();
        const result = await getQueryTimeseriesForNodeUseCase({
          group: ds.group,
          source: ds.source,
          frequency: "raw",
          from,
          to,
        });
        // Stamp the result with THIS `tickId` regardless of when the
        // promise resolves. A late arrival still updates late (lost to
        // any newer tick the guard sees as fresher).
        const stamp = tickId;
        const values = (result.data as { values?: QueryValue[] } | undefined)
          ?.values;
        const last = values?.[values.length - 1];
        if (
          result.success &&
          last &&
          (typeof last.value === "number" || typeof last.value === "string")
        ) {
          guard(stamp, {
            value: last.value,
            lastTimestamp: last.time ?? null,
            hasNoData: false,
            isLoading: false,
            error: undefined,
          });
        } else if (result.success) {
          guard(stamp, {
            value: null,
            lastTimestamp: null,
            hasNoData: true,
            isLoading: false,
            error: undefined,
          });
        } else {
          guard(stamp, {
            value: null,
            lastTimestamp: null,
            hasNoData: false,
            isLoading: false,
            error: result.error,
          });
        }
      } catch (e) {
        guard(tickId, {
          value: null,
          lastTimestamp: null,
          hasNoData: false,
          isLoading: false,
          error: String(e),
        });
      }
    };
    latestLoadRef.current = load;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickId, queryKey]);
  return state;
};

/** Multi-slot fetcher used by MetricRow. Fires ONE Promise.all per tick
 *  for all the slots the formula needs; each resolved promise gets
 *  recorded under its tickId. The result map is bounded to the last 8
 *  ticks so memory can't grow unbounded over a long session. The
 *  latest tick where ALL required slots have non-null values is
 *  exposed separately — metric rows display the value only when the
 *  latest tick is complete so partial-poll renders show "—" instead
 *  of a mixed-tick number. Per-fetch promises are NOT cancelled when
 *  the next tick fires; each tick's results get stamped by `tickId`
 *  even if they resolve later, so a slow node can still contribute
 *  to a complete tick whose timestamp is older than the current
 *  tickId. */
const useTickFetchMany = <F extends string>({
  tickId,
  queriesByField,
  durationMs,
  inViewport,
  preview,
}: {
  tickId: number;
  queriesByField: Partial<Record<F, QuerySpec | null | undefined>>;
  durationMs: number;
  inViewport: boolean;
  preview: boolean;
}): {
  valuesByTick: Record<number, Partial<Record<F, number | null>>>;
} => {
  const [valuesByTick, setValuesByTick] = useState<
    Record<number, Partial<Record<F, number | null>>>
  >({});

  // `queriesByField`/`durationMs`/`preview`/`inViewport` close over
  // via refs so the effect fires ONLY when `tickId` advances — not on
  // every parent re-render. Without this, sliceQueryForField's fresh
  // object identity each render would ping-pong the effect into an
  // infinite fetch loop.
  const queriesByFieldRef = React.useRef(queriesByField);
  queriesByFieldRef.current = queriesByField;
  const durationMsRef = React.useRef(durationMs);
  durationMsRef.current = durationMs;
  const previewRef = React.useRef(preview);
  previewRef.current = preview;
  const inViewportRef = React.useRef(inViewport);
  inViewportRef.current = inViewport;
  // Same as fetchEpochRef in useTickFetchValue: gates stale
  // Promise.all writes when two ticks fire close together.
  const fetchEpochRef = React.useRef(0);
  // Stable content key for the multi-Promise.all: rolling this
  // back into an effect dep lets us wipe old tick records on save so
  // `latestCompleteTickId` can't keep displaying a previous node's
  // computed value until pruning (8 ticks later) drops it.
  const queriesKey = React.useMemo(() => {
    if (!queriesByField) return "";
    return Object.keys(queriesByField)
      .sort()
      .map((k) => {
        const q = queriesByField[k];
        return `${k}:${q?.source ?? ""}|${q?.bindings?.[0]?.source ?? ""}|${
          q?.bindings?.[0]?.address ?? ""
        }`;
      })
      .join(";");
  }, [queriesByField]);
  useEffect(() => {
    if (!queriesKey) return;
    setValuesByTick({});
  }, [queriesKey]);

  useEffect(() => {
    const epoch = ++fetchEpochRef.current;
    const currentQueries = queriesByFieldRef.current;
    const currentDurationMs = durationMsRef.current;
    const currentPreview = previewRef.current;
    const currentInViewport = inViewportRef.current;
    if (tickId === 0) return;
    if (currentPreview || !currentInViewport) return;
    const entries = Object.entries(currentQueries) as Array<
      [F, QuerySpec | null | undefined]
    >;
    if (entries.length === 0) return;
    // Promise.all results are stamped under `tickId` regardless of
    // when they resolve. We DO NOT cancel when the next tick fires —
    // a complete-but-late tick still wins (see header comment).
    const stamp = tickId;
    void (async () => {
      const results = await Promise.all(
        entries.map(async ([field, q]): Promise<[F, number | null]> => {
          if (q == null) return [field, null];
          const ds = getWidgetDataSource(q);
          if (!ds) return [field, null];
          try {
            const to = new Date().toISOString();
            const from = new Date(Date.now() - currentDurationMs).toISOString();
            const result = await getQueryTimeseriesForNodeUseCase({
              group: ds.group,
              source: ds.source,
              frequency: "raw",
              from,
              to,
            });
            if (!result.success) return [field, null];
            const values = (
              result.data as { values?: QueryValue[] } | undefined
            )?.values;
            const last = values?.[values.length - 1];
            return [
              field,
              last && typeof last.value === "number" ? last.value : null,
            ];
          } catch {
            return [field, null];
          }
        }),
      );
      setValuesByTick((prev) => {
        // Drop stale responses from an older effect run.
        if (fetchEpochRef.current !== epoch) return prev;
        const next: Record<number, Partial<Record<F, number | null>>> = {
          ...prev,
          [stamp]: Object.fromEntries(results) as Partial<
            Record<F, number | null>
          >,
        };
        const keys = Object.keys(next)
          .map(Number)
          .sort((a, b) => b - a);
        // Keep at most the last 8 tick records so a long session can't
        // balloon memory. We keep the latest regardless of completeness
        // because it might still be the latest COMPLETE one (a slow
        // node hasn't filled in yet). Pruning 8-tick-old entries
        // forfeits a stale but complete record; in practice the
        // polling cadence keeps old records from being useful by then.
        if (keys.length > 8) {
          for (const k of keys.slice(8)) {
            // Ponytail: prefer pruning incomplete records first so a
            // complete-but-old record survives if the latest is partial.
            if (
              (next[k] &&
                Object.values(next[k] ?? {}).some((v) => v == null)) ||
              Object.values(next[k] ?? {}).length === 0
            ) {
              delete next[k];
              break;
            }
          }
          // Recheck in case pruning didn't drop anything (all 8 were
          // complete): drop the oldest unconditionally.
          if (Object.keys(next).length > 8) {
            for (const k of keys.slice(8)) delete next[k];
          }
        }
        return next;
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickId, queriesKey]);

  // The "latest complete tick" is a formula-semantics decision — the
  // gate depends on which slots the FORMULA requires (see
  // `REQUIRED_METRIC_SLOTS` and the MetricRow caller), not on every
  // fetched slot's presence. Optional slots — e.g. `cavities` for
  // remaining_time — are fetched too so the formula gets a value when
  // the user did bind one, but their absence doesn't block the gate.
  // Caller computes the gate across `valuesByTick`.
  return { valuesByTick };
};

const cssClassName = "machine-overview-widget";

/** Fields the widget always renders as fixed slots (cycles/progress/status
 *  badge) — derived from the same constant the FE/BE use to recognize them
 *  as "structural, not user-added". */
const FIXED_SLOT_FIELDS: Set<string> = new Set(
  MACHINE_OVERVIEW_FIXED_BINDING_KEYS,
);

/** English-fallback string for the cycles row label (rendered above the
 *  value ${good}/${total}). Cycle/progress labels stay field-keyed in
 *  `presentation.row_labels` for now — non-i18n'd defaults. Move to i18n
 *  when a non-English locale ships a translated variant. */
const DEFAULT_CYCLES_LABEL = "target part quantity (good / total)";
const DEFAULT_PROGRESS_LABEL = "progress";

const formatInteger = (value: number, locale: string) =>
  value.toLocaleString(locale);

/** True only for a non-empty string. Used to gate the progress bar's CSS
 *  custom-prop overrides — `null` / `undefined` / `""` all fall through to
 *  the SCSS gradient stops. A future variant with more stops reuses this so
 *  the truthiness rule stays in one place. */
const pickInteresting = (v: unknown): v is string =>
  typeof v === "string" && v.length > 0;

const sliceQueryForField = (
  query: WidgetRendererProps["query"],
  field: string,
): QuerySpec | null => {
  if (!query) return null;
  const binding: QueryBinding | undefined = query.bindings?.find(
    (b) => b.field === field,
  );
  if (!binding) return null;
  return { ...query, bindings: [binding] };
};

/** Legacy-widget fallback: synthesize `MachineOverviewRow[]` from a flat
 *  list of bindings, skipping the 3 fixed slots AND any metric slot
 *  bindings (field names `metric_<id>_<slot>`). Used when the widget
 *  instance doesn't carry `encoding.rows` (pre-migration widgets OR
 *  a saved widget with metrics + a deliberately-empty
 *  `encoding.rows`). Stable IDs derived from `${idx}-${field}` so the
 *  same binding always maps to the same row across renders — and so
 *  a save cycle overwrites the legacy field-keyed `row_labels` with
 *  proper id-keyed ones. Metric slots are intentionally NOT
 *  synthesised here: they're not user-visible math rows, they're
 *  slot bindings the renderer reads via MetricRow's per-slot hooks.
 *  The `metric_` prefix-filter here mirrors the editor's `rows`
 *  seed loop (machine-overview-editor.tsx) — keeping the two in
 *  sync is critical: without the filter, a saved widget without
 *  `encoding.rows` (e.g. the user removed all dynamic value rows
 *  but kept metrics) would render raw `metric_<id>_<slot>` bindings
 *  as phantom ValueRows and the next save would echo them back
 *  through the editor's seed loop as math rows. */
const METRIC_FIELD_PREFIX = "metric_";

const synthesizeRowsFromBindings = (
  bindings: QueryBinding[] | undefined,
): MachineOverviewRow[] => {
  if (!bindings) return [];
  const out: MachineOverviewRow[] = [];
  let idx = 0;
  for (const b of bindings) {
    if (!b.field || FIXED_SLOT_FIELDS.has(b.field)) continue;
    if (b.field.startsWith(METRIC_FIELD_PREFIX)) continue;
    out.push({
      id: `legacy-r${idx}-${b.field}`,
      field: b.field,
      label: b.field,
    });
    idx++;
  }
  return out;
};

/** One dynamic value row. Own component so React's hook order stays
 *  stable regardless of how many rows the user added/removed — each
 *  instance calls `useTickFetchValue` exactly once, driven by the
 *  widget-level `tickId` so all rows share the same polling clock. */
const ValueRow = ({
  row,
  label,
  unitLabel,
  query,
  transforms,
  tickId,
  durationMs,
  inViewport,
  preview,
}: {
  row: MachineOverviewRow;
  label: string;
  /** Resolved unit symbol (already looked up against the global units
   *  catalog by the parent widget) — empty when the row opted out
   *  (no user pick) or picked an unknown id. */
  unitLabel: string;
  query: WidgetRendererProps["query"];
  /** Per-field scale factors stored on `encoding.transforms`. */
  transforms: WidgetRendererProps["encoding"]["transforms"];
  tickId: number;
  durationMs: number;
  inViewport: boolean;
  preview: boolean;
}) => {
  const slotQuery = sliceQueryForField(query, row.field);
  const { value } = useTickFetchValue({
    tickId,
    query: preview ? undefined : slotQuery,
    durationMs,
    inViewport,
    preview,
  });
  // Scale applies to numeric values only; strings pass through. Applied to
  // the fetched value (not the formatted text) so the row and any future
  // consumer of the value agree.
  const scaled = applyFieldTransforms(row.field, value, transforms);
  const display =
    scaled == null
      ? "—"
      : typeof scaled === "number"
      ? scaled.toLocaleString("en")
      : String(scaled);
  // Append the unit symbol after a literal space when present — the
  // existing `__value` cell already governs typography and right-
  // alignment, so a plain text append keeps this self-contained
  // without a new CSS rule. Empty-state `value == null` (em-dash)
  // drops the unit so a unitless placeholder stays unitless.
  const fullDisplay =
    scaled == null ? display : unitLabel ? `${display} ${unitLabel}` : display;
  return (
    <div className={styles[`${cssClassName}__row`]}>
      <span>{label}</span>
      <span className={styles[`${cssClassName}__value`]}>{fullDisplay}</span>
    </div>
  );
};

/** One calculated metric row. Fires one Promise.all per tick across
 *  the formula's required slots (driven by the widget-level `tickId`).
 *  The metric value is gated on the LATEST COMPLETE tick: only when
 *  every required slot has returned a non-null value for the same
 *  tickId do we render a number — partial results render as "—".
 *  Same row class so the metric visually aligns with the dynamic
 *  rows. */
const MetricRow = ({
  metric,
  query,
  tickId,
  durationMs,
  inViewport,
  preview,
  t,
}: {
  metric: MachineOverviewMetric;
  query: WidgetRendererProps["query"];
  tickId: number;
  durationMs: number;
  inViewport: boolean;
  preview: boolean;
  t: (key: string) => string;
}) => {
  // Required slots for THIS kind. YAGNI for kinds the editor can't
  // produce today; those render an empty value row.
  const slots: MachineOverviewMetricSlot[] =
    metric.kind === "remaining_time"
      ? ["cycle_time", "total_parts", "good_parts", "cavities"]
      : metric.kind === "quality"
      ? ["good_parts", "bad_parts"]
      : [];
  // Subset the FORMULA actually requires for a non-null result.
  // `cavities` is optional in `computeRemainingTime` — the formula
  // omits the divide when cavities is missing. Blocking the gate on
  // cavities would leave the row permanently on "—" when the user
  // doesn't bind that node (which is the default UX). This list is
  // the source of truth for "latest complete tick" below.
  const requiredForFormula: MachineOverviewMetricSlot[] =
    REQUIRED_METRIC_SLOTS[metric.kind] ?? [];

  // Build the per-slot query sub-specs ONCE per metric/id + preview
  // combo. Memoized so the fetcher hook's effect doesn't re-fire on
  // every parent re-render when the query object identity changes
  // but the underlying address list is identical.
  const queriesByField = useMemo(() => {
    if (preview)
      return {} as Partial<Record<MachineOverviewMetricSlot, QuerySpec | null>>;
    const out: Partial<Record<MachineOverviewMetricSlot, QuerySpec | null>> =
      {};
    for (const slot of slots) {
      out[slot] = sliceQueryForField(query, metricSlotField(metric.id, slot));
    }
    return out;
    // `slots` is derived from `metric.kind` so listing both is redundant,
    // but they ensure correct gating on kind-switch. eslint can't read
    // the derivation so we list both intentionally.
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [metric.id, metric.kind, query, preview, slots]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const { valuesByTick } = useTickFetchMany<MachineOverviewMetricSlot>({
    tickId,
    queriesByField,
    durationMs,
    inViewport,
    preview,
  });
  // Completeness gate: pick the latest tick whose FORMULA-required
  // slots are non-null. Optional slots (e.g. `cavities` for
  // remaining_time) are fetched too, but their absence doesn't block
  // the gate — the formula explicitly omits the divide when cavities
  // is null (see computeRemainingTime).
  // Re-runs when the result map updates OR when a kind swap changes
  // the required-for-formula set (records from old-shape ticks may no
  // longer satisfy the new criterion).
  const requiredKey = requiredForFormula.join("|");
  /* eslint-disable react-hooks/exhaustive-deps */
  const latestCompleteTickId = useMemo(() => {
    let best: number | null = null;
    for (const [t, vals] of Object.entries(valuesByTick)) {
      const tn = +t;
      const allRequiredPresent = requiredForFormula.every(
        (s) => vals[s] != null,
      );
      if (allRequiredPresent && (best === null || tn > best)) best = tn;
    }
    return best;
  }, [valuesByTick, requiredKey]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const completeSlotValues =
    latestCompleteTickId != null ? valuesByTick[latestCompleteTickId] : null;
  const result =
    slots.length === 0 || !completeSlotValues
      ? null
      : computeMetric(metric, completeSlotValues);
  const display =
    result == null
      ? "—"
      : metric.kind === "remaining_time"
      ? `${result.toFixed(1)} h`
      : `${result.toFixed(1)} %`;
  return (
    <div className={styles[`${cssClassName}__row`]}>
      <span>
        {t(`modules.machineOverviewWidget.metrics.kind.${metric.kind}`)}
      </span>
      <span className={styles[`${cssClassName}__value`]}>{display}</span>
    </div>
  );
};

/**
 * Machine Overview widget. Reads good/total (cycles + progress) and
 * status (badge) via 3 fixed top-level hooks, then renders one dynamic
 * value row per entry in `widget.encoding.rows`. New widgets carry the
 * template's defaults inside `encoding.rows[]`; legacy widgets missing
 * `encoding.rows` get a synthesized row list from `query.bindings` as a
 * fallback (synthesized rows get a fixed id so saves migrate them
 * cleanly on next round-trip).
 *
 * Row count is whatever the widget instance says it is — the renderer
 * doesn't ship a magic number. Each row is read by its own hook inside
 * `ValueRow`, so React's hook order stays stable regardless of how many
 * rows the user added/removed.
 */

/** Dashboard link button kept in its own component so `useNavigate` only
 *  runs when a link is actually rendered. The widget picker previews this
 *  widget inside `ModalsEntryPoint`, which sits OUTSIDE `RouterProvider` —
 *  calling the router hook in the parent would throw there and unmount the
 *  whole React tree, taking down the static-tool library panel along with
 *  it. Mounting the link in a child gated on `!preview && dashboardId` keeps
 *  the picker preview safe. */
const DashboardLinkButton = ({ dashboardId }: { dashboardId: string }) => {
  const navigate = useNavigate();
  return (
    <IconButton
      icon={linkIcon}
      variant="link"
      fun={() => navigate(getDashboardPath(dashboardId))}
    />
  );
};

export const MachineOverviewWidget = ({
  title,
  encoding,
  semantics,
  presentation,
  image_url,
  query,
  preview = false,
  showTitle = true,
  inViewport = true,
}: WidgetRendererProps): React.ReactElement => {
  const i18n = useI18N();
  const lastUpdatedText = i18n("modules.machineOverviewWidget.lastUpdated");
  const pres = presentation ?? {};
  // The parent owns the units slice subscription so per-row children
  // don't need a Redux context — keeps `ValueRow` plain props and
  // proves the same selector stays consistent across rows.
  const units = useSelector(selectUnitsById);
  // Per-row unit label pre-resolution. Computed inline because the
  // parent already iterates sortedDisplayItems to feed ValueRow;
  // squeezing it into a Record wouldn't change the runtime shape
  // and would just add an object pooling pass.
  // Optional link to another dashboard, chosen in the edit drawer. Stored as
  // a UUID in `presentation.dashboard_id`; the path shape stays in
  // `getDashboardPath`. Unknown type (legacy/hand-edited) is ignored.
  const dashboardId =
    typeof pres.dashboard_id === "string" ? pres.dashboard_id : null;
  const sem = isMachineOverviewSemantics(semantics)
    ? semantics
    : { status_entries: [] };
  // Top-level widget.image_url is the canonical source — BE writes a
  // fresh URL there after each multipart PUT that includes an `image`
  // part. `presentation.image_url` is deprecated and ignored.
  const imageUrl = image_url ?? null;
  // Default to "badge" for legacy widgets created before this knob; only
  // an exact "background" string flips into the new branch.
  const displayMode: MachineOverviewDisplayMode =
    pres.status_display_mode === "background" ? "background" : "badge";
  const durationMs =
    isoToDurationMs(query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS;

  // One widget-level polling clock. Drives every fetch the widget
  // makes (3 fixed slots + N dynamic value rows + M metric rows); all
  // child hooks listen on `tickId` and fire in lockstep with the
  // polling timeout. Visibility is gated at the parent: off-screen
  // widgets drop the clock entirely.
  const pollingMs = resolvePollingMs(
    query?.sampling?.frequency,
    query?.sampling?.polling_interval,
  );
  const tickId = useWidgetPollingClock(pollingMs, inViewport);

  // 3 fixed slots driven by the clock. Each holds its own state; the
  // timestamp on the status row is rendered under the cycles/progress
  // block (uses the same `lastTimestamp` as before — just stamped with
  // the latest tickId that returned a value).
  const good = useTickFetchValue({
    tickId,
    query: preview ? undefined : sliceQueryForField(query, "good"),
    durationMs,
    inViewport,
    preview,
  });
  const total = useTickFetchValue({
    tickId,
    query: preview ? undefined : sliceQueryForField(query, "total"),
    durationMs,
    inViewport,
    preview,
  });
  const status = useTickFetchValue({
    tickId,
    query: preview ? undefined : sliceQueryForField(query, "status"),
    durationMs,
    inViewport,
    preview,
  });

  // Preview/placeholder: hooks return null without a query, so display
  // plausible dummy values instead of em-dashes. Cycles/total picked so the
  // progress bar reads healthy (127/200 = 63.5%) — closer to a populated
  // tile than a wall of "—". Status badge stays absent in preview because
  // the template semantics is empty, so `matchStatusEntry` correctly
  // returns null (and a fake matched entry would clash with the same
  // `:has()` selector that drives the colored BG mode toggle).
  // Cycles/progress are counters — a string node bound here can't feed
  // the arithmetic, so it reads as "—" like any missing slot.
  // Dynamic value rows — prefer `encoding.rows[]` (the source of truth
  // shipped with the template). Falling back to a synthesis from
  // `query.bindings` lets legacy widgets (no `encoding.rows` yet) keep
  // working until their first save migrates them forward.
  const transforms = encoding?.transforms;
  const resolveGood = preview
    ? 127
    : applyFieldTransforms("good", good.value, transforms);
  const resolveTotal = preview
    ? 200
    : applyFieldTransforms("total", total.value, transforms);
  const displayGood = typeof resolveGood === "number" ? resolveGood : null;
  const displayTotal = typeof resolveTotal === "number" ? resolveTotal : null;

  const matched = matchStatusEntry(sem.status_entries, status.value);

  // Preview/placeholder badge: the picker template ships with no
  // status_entries, so `matched` is null and the status row would render
  // empty. Fill it with a sample ("Automatic" + doneGreen) so the preview
  // tile looks like a populated status. Real production widgets stay
  // untouched — their badge is driven by actual user bindings. `as const`
  // narrows the literal to the StatusColorToken union (defined in
  // @src/types/dashboards) without re-exporting the type.
  const previewBadge =
    preview && !matched
      ? ({ label: "Automatic", color_token: "doneGreen" } as const)
      : null;
  const renderBadge = matched ?? previewBadge;
  // BG mode paints the FULL tile: `__tile-content` (the parent grid cell)
  // sets background-color via `:has([data-mo-status=<token>])` — see
  // dashboard-tile-grid.module.scss. The widget stamps the matched
  // token on the section as `data-mo-status` so that selector hooks;
  // nothing else here — widget_primary / widget_secondary the user
  // picked at the picker drive the text colors. If they pick a low-
  // contrast combination (e.g. primary=white on bg=red) the rendering
  // is whatever they chose; we don't override.
  const isBgMode = displayMode === "background" && !!matched;
  // Dynamic value rows — prefer `encoding.rows[]` (the source of truth
  // shipped with the template). Falling back to a synthesis from
  // `query.bindings` lets legacy widgets (no `encoding.rows` yet) keep
  // working until their first save migrates them forward.
  const rows: MachineOverviewRow[] =
    encoding?.rows && encoding.rows.length > 0
      ? encoding.rows
      : synthesizeRowsFromBindings(query?.bindings);
  const resolveRowLabel = (row: MachineOverviewRow) =>
    resolveRowLabelRaw(row, pres.row_labels, i18n);
  // Calculated metric cards — ordered by `order` so the user's chosen
  // sequence is preserved across renders. Empty for widgets that
  // pre-date metrics (encoding.metrics undefined) or for tiles where
  // the user removed all metrics.
  const metrics: MachineOverviewMetric[] = sortMetricsForDisplay(
    encoding?.metrics,
  );

  // Merged chronological render list — user-added rows + metric cards
  // interleaved by `order` so a row added after a metric appears
  // AFTER the metric (and vice-versa). Rows without `order` (saved
  // widgets predating the field) sort to -Infinity so the legacy
  // "rows first, metrics after" layout is preserved for legacy
  // widgets. Same pattern as the editor drawer's interleaving, so
  // the widget preview and the dashboard tile render consistently.
  type DisplayItem =
    | { kind: "row"; row: MachineOverviewRow }
    | { kind: "metric"; metric: MachineOverviewMetric };
  const sortedDisplayItems: DisplayItem[] = useMemo(
    () =>
      [
        ...rows.map((r) => ({
          kind: "row" as const,
          row: r,
          sortKey: r.order ?? Number.NEGATIVE_INFINITY,
        })),
        ...metrics.map((m) => ({
          kind: "metric" as const,
          metric: m,
          sortKey: m.order ?? Number.NEGATIVE_INFINITY,
        })),
      ].sort((a, b) => a.sortKey - b.sortKey),
    [rows, metrics],
  );

  // The widget is “empty” only when nothing is configured at all (no binding
  // carries an address) — then a fresh tile would show all rows as “—”, so
  // the splash is the honest state. Once ANY node is picked, render the rows
  // and let each fetch its own value (a bound-but-empty row shows “—”). Row
  // children run their own fetches + loading states, so the parent's spinner
  // only reflects the fixed good/total/status hooks.
  const anyConfiguredBinding = (query?.bindings ?? []).some((b) => !!b.address);
  const isLoading =
    !preview &&
    !!query &&
    (good.isLoading || total.isLoading || status.isLoading);
  const allEmpty = !preview && !!query && !anyConfiguredBinding;

  const progress =
    displayGood != null && displayTotal != null && displayTotal > 0
      ? (displayGood / displayTotal) * 100
      : null;
  // Start and end colors for the progress-bar gradient, read from
  // `presentation.progress_color_{start,end}`. The editor writes
  // both keys on every save, so a current widget always carries two
  // hexes (or — for legacy widgets predating this feature — no keys
  // at all). Per-key fallback remains for the legacy case AND to
  // tolerate empty/null on a hand-edited BE record. `pickInteresting`
  // keeps the truthiness check consistent between both stops a
  // single touch point handles a future expansion (e.g. middle
  // stop) without per-band copy/paste.
  const progressFillStyle: React.CSSProperties = {
    width: `${progress ?? 0}%`,
    ...(pickInteresting(pres.progress_color_start)
      ? { ["--mo-progress-fill-start"]: pres.progress_color_start }
      : {}),
    ...(pickInteresting(pres.progress_color_end)
      ? { ["--mo-progress-fill-end"]: pres.progress_color_end }
      : {}),
  };

  return (
    <section
      className={styles[cssClassName]}
      data-mo-status={isBgMode ? matched.color_token : undefined}
    >
      <div className={styles[`${cssClassName}__header`]}>
        {/* No image container at all when image_url is null, except in
            picker preview mode where we fall back to tt-logo so the tile has
            something visible. */}
        {imageUrl ? (
          <img
            className={styles[`${cssClassName}__image`]}
            src={imageUrl}
            alt=""
          />
        ) : preview ? (
          <img
            className={styles[`${cssClassName}__image`]}
            src={ttLogo}
            alt=""
          />
        ) : null}
        {showTitle && (
          <h3 className={styles[`${cssClassName}__title`]}>{title || ""}</h3>
        )}
        {/* Link to the configured dashboard, right after the title. Lives in
            a child component so `useNavigate` is only called when a link is
            actually rendered — the picker preview must NOT trigger it. */}
        {!preview && dashboardId && (
          <DashboardLinkButton dashboardId={dashboardId} />
        )}
      </div>

      {displayMode === "badge" && renderBadge ? (
        <button
          className={styles[`${cssClassName}__badge`]}
          style={getStatusBadgeStyle(renderBadge.color_token)}
          type="button"
        >
          {renderBadge.label}
        </button>
      ) : null}

      {isLoading ? (
        <div className={styles[`${cssClassName}__spinner-wrapper`]}>
          <Spinner />
        </div>
      ) : allEmpty ? (
        <NoDataInTimeframe />
      ) : (
        <>
          <div className={styles[`${cssClassName}__row`]}>
            <span>{pres.row_labels?.cycles ?? DEFAULT_CYCLES_LABEL}</span>
            <span className={styles[`${cssClassName}__value`]}>
              {displayGood != null && displayTotal != null
                ? `${formatInteger(displayGood, "en")} / ${formatInteger(
                    displayTotal,
                    "en",
                  )}`
                : "— / —"}
            </span>
          </div>
          <div className={styles[`${cssClassName}__progress-track`]}>
            <div
              className={styles[`${cssClassName}__progress-fill`]}
              style={progressFillStyle}
            />
          </div>
          <div className={styles[`${cssClassName}__row`]}>
            <span>{pres.row_labels?.progress ?? DEFAULT_PROGRESS_LABEL}</span>
            <span className={styles[`${cssClassName}__value`]}>
              {progress != null ? `${progress.toFixed(1)}%` : "—"}
            </span>
          </div>
          {/* User-added items rendered in one chronological sequence:
              value rows + metric cards sorted by `order` (see
              sortedDisplayItems above). Discriminator picks the right
              React component per kind; both reuse the existing
              ValueRow / MetricRow components unchanged. */}
          {sortedDisplayItems.map((item) =>
            item.kind === "row" ? (
              <ValueRow
                key={item.row.id}
                row={item.row}
                label={resolveRowLabel(item.row)}
                unitLabel={
                  item.row.unit == null
                    ? ""
                    : unitLabelOf(getUnitById(units, item.row.unit))
                }
                query={query}
                transforms={transforms}
                tickId={tickId}
                durationMs={durationMs}
                inViewport={inViewport}
                preview={preview}
              />
            ) : (
              <MetricRow
                key={item.metric.id}
                metric={item.metric}
                query={query}
                tickId={tickId}
                durationMs={durationMs}
                inViewport={inViewport}
                preview={preview}
                t={i18n}
              />
            ),
          )}
        </>
      )}
      {/* "Last updated" reflects ONLY the status node — that's the
       * canonical "when did the machine last report its opMode". Cycles
       * and per-row counters lag behind status on event boundaries, so
       * max-across-all would lie when cycles poll faster than status.
       * Hidden when the hook has no data yet (preview / first load) and
       * when the fetch errored, so a stale-cached timestamp never sits
       * below a red error banner. */}
      {status.lastTimestamp && !status.error && !preview && (
        <p className={styles[`${cssClassName}__updated-at`]}>
          {lastUpdatedText}: <TimezoneDate date={status.lastTimestamp} />
        </p>
      )}
    </section>
  );
};
